import { useState, type FormEvent } from 'react';
import { X, Wrench, Search, Zap, Users, ChevronDown, ChevronUp, Shield, Lock } from 'lucide-react';
import type { Agent } from '../hooks/useAgents';
import type { CreateTeamDto, RunnerStrategy } from '../types/team';
import { useSshHosts } from '../hooks/useSshHosts';

const ROLE_ICONS = {
  creator: Wrench,
  executor: Zap,
  researcher: Search,
} as const;

const ROLE_BADGES = {
  creator: 'bg-blue-100 text-blue-700',
  executor: 'bg-green-100 text-green-700',
  researcher: 'bg-purple-100 text-purple-700',
} as const;

const PERMISSION_MODES = [
  { value: 'acceptEdits', label: 'Accept Edits' },
  { value: 'plan', label: 'Plan Mode (read-only)' },
  { value: 'bypassPermissions', label: 'Bypass Permissions' },
];

const SPAWN_MODES = [
  { value: 'sdk', label: 'This server (SDK)' },
  { value: 'cli', label: 'Local CLI' },
  { value: 'ssh', label: 'SSH' },
  { value: 'host', label: 'Host Runner' },
];

const RUNNER_STRATEGIES: { value: RunnerStrategy; label: string }[] = [
  { value: 'local', label: 'Local' },
  { value: 'ssh', label: 'SSH' },
  { value: 'mixed', label: 'Mixed' },
];

interface Task {
  id: string;
  title: string;
  status: string;
}

interface CreateTeamModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreateTeamDto) => Promise<void>;
  agents: Agent[];
  tasks?: Task[];
  isPending?: boolean;
}

export default function CreateTeamModal({
  open,
  onClose,
  onSubmit,
  agents,
  tasks,
  isPending,
}: CreateTeamModalProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedAgentIds, setSelectedAgentIds] = useState<Set<string>>(new Set());
  const [leadAgentId, setLeadAgentId] = useState<string>('');
  const [selectedTaskIds, setSelectedTaskIds] = useState<Set<string>>(new Set());

  // Advanced config
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [runtime, setRuntime] = useState<string>('claude');
  const [permissionMode, setPermissionMode] = useState('acceptEdits');
  const [spawnMode, setSpawnMode] = useState('sdk');
  const [runnerStrategy, setRunnerStrategy] = useState<RunnerStrategy>('local');
  const [selectedSshHostIds, setSelectedSshHostIds] = useState<Set<string>>(new Set());
  const [encryptionEnabled, setEncryptionEnabled] = useState(false);

  const { data: sshHosts } = useSshHosts();

  if (!open) return null;

  const toggleAgent = (id: string) => {
    setSelectedAgentIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        if (leadAgentId === id) setLeadAgentId('');
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleTask = (id: string) => {
    setSelectedTaskIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSshHost = (id: string) => {
    setSelectedSshHostIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim() || selectedAgentIds.size === 0) return;

    const dto: CreateTeamDto = {
      name: name.trim(),
      description: description.trim() || undefined,
      agentIds: Array.from(selectedAgentIds),
      taskIds: selectedTaskIds.size > 0 ? Array.from(selectedTaskIds) : undefined,
      leadAgentId: leadAgentId || undefined,
      runtime,
      permissionMode,
      spawnMode,
      runnerStrategy: showAdvanced ? runnerStrategy : undefined,
      preferredSshHostIds: selectedSshHostIds.size > 0 ? Array.from(selectedSshHostIds) : undefined,
      encryptionEnabled: encryptionEnabled || undefined,
    };

    await onSubmit(dto);

    // Reset form
    setName('');
    setDescription('');
    setSelectedAgentIds(new Set());
    setLeadAgentId('');
    setSelectedTaskIds(new Set());
    setShowAdvanced(false);
    setRuntime('claude');
    setPermissionMode('acceptEdits');
    setSpawnMode('sdk');
    setRunnerStrategy('local');
    setSelectedSshHostIds(new Set());
    setEncryptionEnabled(false);
    onClose();
  };

  const openTasks = tasks?.filter(t => t.status === 'todo' || t.status === 'in-progress') ?? [];
  const selectedAgents = agents.filter(a => selectedAgentIds.has(a.id));
  const showSshHosts = spawnMode === 'ssh' || runnerStrategy === 'ssh' || runnerStrategy === 'mixed';
  const connectedHosts = sshHosts?.filter(h => h.status === 'connected') ?? [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-xl rounded-xl border border-slate-200 bg-white shadow-lg max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4 shrink-0">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-slate-600" />
            <h2 className="text-lg font-semibold text-slate-900">Create Team</h2>
          </div>
          <button onClick={onClose} className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* Team name & description */}
          <div className="space-y-3">
            <div>
              <label htmlFor="team-name" className="mb-1.5 block text-sm font-medium text-slate-700">Team Name</label>
              <input
                id="team-name"
                type="text"
                required
                value={name}
                onChange={e => setName(e.target.value)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-slate-500 focus:outline-none"
                placeholder="e.g. Feature Sprint, Bug Hunt"
              />
            </div>
            <div>
              <label htmlFor="team-desc" className="mb-1.5 block text-sm font-medium text-slate-700">Description</label>
              <textarea
                id="team-desc"
                rows={2}
                value={description}
                onChange={e => setDescription(e.target.value)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-slate-500 focus:outline-none"
                placeholder="What will this team work on?"
              />
            </div>
          </div>

          {/* Agent selection */}
          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700">
              Select Agents <span className="text-slate-400">({selectedAgentIds.size} selected)</span>
            </label>
            <div className="max-h-48 space-y-1 overflow-y-auto rounded-lg border border-slate-200 p-2">
              {agents.length === 0 ? (
                <p className="py-4 text-center text-sm text-slate-400">No agents available</p>
              ) : (
                agents.map(agent => {
                  const RoleIcon = ROLE_ICONS[agent.role as keyof typeof ROLE_ICONS] || Zap;
                  const badge = ROLE_BADGES[agent.role as keyof typeof ROLE_BADGES] || ROLE_BADGES.executor;
                  const checked = selectedAgentIds.has(agent.id);

                  return (
                    <label
                      key={agent.id}
                      className={`flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 transition-colors ${
                        checked ? 'bg-slate-50' : 'hover:bg-slate-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleAgent(agent.id)}
                        className="rounded border-slate-300"
                      />
                      <RoleIcon className="h-4 w-4 text-slate-400" />
                      <span className="flex-1 text-sm text-slate-900">{agent.name}</span>
                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${badge}`}>
                        {agent.role}
                      </span>
                    </label>
                  );
                })
              )}
            </div>
          </div>

          {/* Lead selection */}
          {selectedAgents.length > 1 && (
            <div>
              <label htmlFor="team-lead" className="mb-1.5 block text-sm font-medium text-slate-700">
                Team Lead <span className="text-slate-400">(optional)</span>
              </label>
              <select
                id="team-lead"
                value={leadAgentId}
                onChange={e => setLeadAgentId(e.target.value)}
                className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-slate-500 focus:outline-none"
              >
                <option value="">No lead</option>
                {selectedAgents.map(a => (
                  <option key={a.id} value={a.id}>{a.name} ({a.role})</option>
                ))}
              </select>
            </div>
          )}

          {/* Task assignment */}
          {openTasks.length > 0 && (
            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700">
                Assign Tasks <span className="text-slate-400">(optional, {selectedTaskIds.size} selected)</span>
              </label>
              <div className="max-h-36 space-y-1 overflow-y-auto rounded-lg border border-slate-200 p-2">
                {openTasks.map(task => (
                  <label
                    key={task.id}
                    className={`flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 transition-colors ${
                      selectedTaskIds.has(task.id) ? 'bg-slate-50' : 'hover:bg-slate-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedTaskIds.has(task.id)}
                      onChange={() => toggleTask(task.id)}
                      className="rounded border-slate-300"
                    />
                    <span className="flex-1 truncate text-sm text-slate-900">{task.title}</span>
                    <span className="text-xs capitalize text-slate-400">{task.status}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Advanced Configuration toggle */}
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700"
          >
            {showAdvanced ? (
              <>
                <ChevronUp className="h-4 w-4" />
                Hide advanced configuration
              </>
            ) : (
              <>
                <ChevronDown className="h-4 w-4" />
                Advanced configuration
              </>
            )}
          </button>

          {showAdvanced && (
            <div className="space-y-4 rounded-lg border border-slate-200 bg-slate-50/50 p-4">
              {/* Runtime */}
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">Runtime</label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setRuntime('claude')}
                    className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition-all ${
                      runtime === 'claude'
                        ? 'border-slate-900 bg-white text-slate-900 ring-1 ring-slate-900'
                        : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                    }`}
                  >
                    Claude
                  </button>
                  <button
                    type="button"
                    onClick={() => setRuntime('codex')}
                    className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium transition-all ${
                      runtime === 'codex'
                        ? 'border-slate-900 bg-white text-slate-900 ring-1 ring-slate-900'
                        : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                    }`}
                  >
                    Codex
                  </button>
                </div>
              </div>

              {/* Permission Mode */}
              <div>
                <label htmlFor="team-perm" className="mb-1.5 block text-sm font-medium text-slate-700">
                  <Shield className="mr-1 inline h-3.5 w-3.5" />
                  Permission Mode
                </label>
                <select
                  id="team-perm"
                  value={permissionMode}
                  onChange={e => setPermissionMode(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-slate-500 focus:outline-none"
                >
                  {PERMISSION_MODES.map(p => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
              </div>

              {/* Spawn Mode */}
              <div>
                <label htmlFor="team-spawn" className="mb-1.5 block text-sm font-medium text-slate-700">
                  Spawn Mode
                </label>
                <select
                  id="team-spawn"
                  value={spawnMode}
                  onChange={e => setSpawnMode(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-slate-500 focus:outline-none"
                >
                  {SPAWN_MODES.map(m => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>

              {/* Runner Strategy */}
              <div>
                <label htmlFor="team-strategy" className="mb-1.5 block text-sm font-medium text-slate-700">
                  Runner Strategy
                </label>
                <select
                  id="team-strategy"
                  value={runnerStrategy}
                  onChange={e => setRunnerStrategy(e.target.value as RunnerStrategy)}
                  className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-slate-500 focus:outline-none"
                >
                  {RUNNER_STRATEGIES.map(s => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>

              {/* SSH Hosts (shown when SSH/Mixed) */}
              {showSshHosts && (
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-slate-700">
                    SSH Hosts <span className="text-slate-400">({selectedSshHostIds.size} selected)</span>
                  </label>
                  {connectedHosts.length === 0 ? (
                    <p className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-400">
                      No connected SSH hosts available
                    </p>
                  ) : (
                    <div className="max-h-32 space-y-1 overflow-y-auto rounded-lg border border-slate-200 bg-white p-2">
                      {connectedHosts.map(host => (
                        <label
                          key={host.id}
                          className={`flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 transition-colors ${
                            selectedSshHostIds.has(host.id) ? 'bg-slate-50' : 'hover:bg-slate-50'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedSshHostIds.has(host.id)}
                            onChange={() => toggleSshHost(host.id)}
                            className="rounded border-slate-300"
                          />
                          <span className="flex-1 text-sm text-slate-900">{host.name}</span>
                          <span className="text-xs text-slate-400">{host.hostname}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Encryption */}
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={encryptionEnabled}
                  onChange={e => setEncryptionEnabled(e.target.checked)}
                  className="rounded border-slate-300"
                />
                <Lock className="h-3.5 w-3.5 text-slate-400" />
                Enable E2E encrypted messaging
              </label>
            </div>
          )}

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isPending || !name.trim() || selectedAgentIds.size === 0}
              className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
            >
              {isPending ? 'Creating...' : 'Create & Launch Team'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
